import { useState, useEffect, useCallback } from "react";
import { json } from "@remix-run/node";
import { Link as RemixLink, useLoaderData } from "@remix-run/react";
import { IconButton, Text } from "@chakra-ui/react";
import { ChevronUpIcon } from "@chakra-ui/icons";

import { Review } from "#app/models/index.js";
import { CallToAction, ReviewGrid } from "#app/components/index.jsx";
import {
  Layout,
  Header,
  Content,
} from "#app/components/layouts/MarketingPage.jsx";
import { getSeoMeta, getBaseUrl, getDefaultOgImage } from "#app/utils/meta.js";

export function meta({ matches }) {
  const baseUrl = getBaseUrl(matches);
  return getSeoMeta({
    title: "Reviews | Schnerp",
    description: "See what campers have to say about Schnerp",
    url: `${baseUrl}/reviews`,
    image: getDefaultOgImage(baseUrl),
  });
}

export async function loader() {
  const [reviews, stats] = await Promise.all([
    Review.getVisibleReviews(),
    Review.getStats(),
  ]);

  return json({ reviews, stats });
}

function BackToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    function onScroll() {
      setVisible(window.scrollY > 600);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <IconButton
      aria-label="Back to top"
      icon={<ChevronUpIcon boxSize={6} />}
      isRound
      colorScheme="brand"
      size="lg"
      position="fixed"
      bottom={{ base: 5, md: 8 }}
      right={{ base: 5, md: 8 }}
      shadow="lg"
      opacity={visible ? 1 : 0}
      pointerEvents={visible ? "auto" : "none"}
      transform={visible ? "scale(1)" : "scale(0.8)"}
      transition="all 0.2s ease-out"
      zIndex={100}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      _hover={{ shadow: "xl", transform: "scale(1.08)" }}
      _active={{ transform: "scale(0.95)" }}
    />
  );
}

export default function Reviews() {
  const { reviews, stats } = useLoaderData();
  const [allLoaded, setAllLoaded] = useState(false);
  const onFullyLoaded = useCallback(() => setAllLoaded(true), []);

  return (
    <Layout>
      <Header
        title="Don't take our word for it"
        subtitle={
          <>
            All{" "}
            <Text as="strong" color="emphasized">
              {stats.total.toLocaleString()} reviews
            </Text>{" "}
            Schnerp has ever received, in one place.{" "}
            <Text as="strong" color="emphasized">
              No edits, no filtering
            </Text>
            , even the cranky ones.
          </>
        }
      />
      <Content mt={{ base: 4, md: 6 }}>
        <ReviewGrid reviews={reviews} onFullyLoaded={onFullyLoaded} />
        {allLoaded && (
          <Text fontSize="sm" color="muted" textAlign="center" pt={6} pb={2}>
            That's every review. {stats.total} from Google
            {stats.facebook > 0 ? " and Facebook" : ""}, unfiltered.
          </Text>
        )}
        {allLoaded && (
          <CallToAction
            heading="Woah, you made it all the way down"
            body="You just read through every single review. At this point you might as well try it."
            buttonText="Fine, I'll sign up"
            buttonProps={{
              as: RemixLink,
              to: "/new",
            }}
          />
        )}
      </Content>
      <BackToTop />
    </Layout>
  );
}
